import { Plus } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";
import type { FormRule } from "../../types/form-rule-types";
import { FormRuleCard } from "./form-rule-card";
import { FormRulesFeaturedIcon } from "./form-rules-featured-icon";

const RuleFilter = {
	all: "all",
	enabled: "enabled",
	disabled: "disabled",
} as const;
type RuleFilterValue = (typeof RuleFilter)[keyof typeof RuleFilter];

const RuleSort = {
	nameAsc: "nameAsc",
	nameDesc: "nameDesc",
	newest: "newest",
	oldest: "oldest",
} as const;
type RuleSortValue = (typeof RuleSort)[keyof typeof RuleSort];

export interface FormRulesListProps {
	rules: FormRule[];
	paths: AdminFormI18nPaths;
	onAdd: () => void;
	onEdit: (id: string) => void;
	onDelete: (id: string) => void;
	onToggleEnabled: (id: string, enabled: boolean) => void;
	isReadOnly?: boolean;
}

/**
 * Top-level presenter for the Form Rules tab body: filter + sort toolbar, empty state,
 * and the rule cards list. Pure UI — no store access. The container handles open/close
 * URL state and store mutations.
 */
export function FormRulesList({
	rules,
	paths,
	onAdd,
	onEdit,
	onDelete,
	onToggleEnabled,
	isReadOnly,
}: FormRulesListProps) {
	const { t } = useAppTranslation();
	const [filter, setFilter] = useState<RuleFilterValue>(RuleFilter.all);
	const [sort, setSort] = useState<RuleSortValue>(RuleSort.nameAsc);

	const visibleRules = useMemo(() => {
		const filtered = rules.filter((r) => {
			if (filter === RuleFilter.enabled) return r.enabled;
			if (filter === RuleFilter.disabled) return !r.enabled;
			return true;
		});
		const sorted = [...filtered];
		sorted.sort((a, b) => {
			switch (sort) {
				case RuleSort.nameAsc:
					return a.name.localeCompare(b.name);
				case RuleSort.nameDesc:
					return b.name.localeCompare(a.name);
				case RuleSort.newest:
					return (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
				case RuleSort.oldest:
					return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
				default:
					return 0;
			}
		});
		return sorted;
	}, [rules, filter, sort]);

	const isEmpty = rules.length === 0;

	return (
		<div className="flex flex-1 flex-col bg-background-primary">
			<header className="flex flex-wrap items-start justify-between gap-3 border-b border-border-secondary px-6 py-4">
				<div className="min-w-0 flex-1">
					<h2 className="text-lg font-semibold text-text-primary">
						{t(paths.formRules.title)}
					</h2>
					<p className="text-sm text-text-tertiary">
						{t(paths.formRules.subtitle)}
					</p>
				</div>
				{isReadOnly ? null : (
					<Button size="sm" color="primary" iconLeading={Plus} onClick={onAdd}>
						{t(paths.formRules.addRule)}
					</Button>
				)}
			</header>

			{isEmpty ? (
				<div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
					<FormRulesFeaturedIcon className="mb-4" />
					<p className="text-sm font-medium text-text-primary">
						{t(paths.formRules.emptyTitle)}
					</p>
					<p className="mt-2 max-w-md text-sm text-text-tertiary">
						{t(paths.formRules.emptySubtitle)}
					</p>
					{isReadOnly ? null : (
						<div className="mt-6">
							<Button
								size="sm"
								color="primary"
								iconLeading={Plus}
								onClick={onAdd}
							>
								{t(paths.formRules.addRule)}
							</Button>
						</div>
					)}
				</div>
			) : (
				<>
					<div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
						<label className="flex items-center gap-2 text-xs text-text-tertiary">
							<span className="font-medium">
								{t(paths.formRules.filters.label)}
							</span>
							<select
								value={filter}
								onChange={(e) => {
									setFilter(e.target.value as RuleFilterValue);
								}}
								className="rounded-md border border-border-secondary bg-background-primary px-2 py-1 text-text-primary"
							>
								<option value={RuleFilter.all}>
									{t(paths.formRules.filters.all)}
								</option>
								<option value={RuleFilter.enabled}>
									{t(paths.formRules.filters.enabledOnly)}
								</option>
								<option value={RuleFilter.disabled}>
									{t(paths.formRules.filters.disabledOnly)}
								</option>
							</select>
						</label>

						<label className="flex items-center gap-2 text-xs text-text-tertiary">
							<span className="font-medium">
								{t(paths.formRules.sort.label)}
							</span>
							<select
								value={sort}
								onChange={(e) => {
									setSort(e.target.value as RuleSortValue);
								}}
								className="rounded-md border border-border-secondary bg-background-primary px-2 py-1 text-text-primary"
							>
								<option value={RuleSort.nameAsc}>
									{t(paths.formRules.sort.nameAsc)}
								</option>
								<option value={RuleSort.nameDesc}>
									{t(paths.formRules.sort.nameDesc)}
								</option>
								<option value={RuleSort.newest}>
									{t(paths.formRules.sort.newest)}
								</option>
								<option value={RuleSort.oldest}>
									{t(paths.formRules.sort.oldest)}
								</option>
							</select>
						</label>
					</div>

					<ul className="flex flex-1 flex-col gap-3 px-6 pb-6">
						{visibleRules.map((rule) => (
							<FormRuleCard
								key={rule.id}
								rule={rule}
								paths={paths}
								onEdit={onEdit}
								onDelete={onDelete}
								onToggleEnabled={onToggleEnabled}
								isReadOnly={isReadOnly}
							/>
						))}
					</ul>
				</>
			)}
		</div>
	);
}
